import React, { useState, useMemo } from 'react';
import { useStore, useNav } from '../store/StoreContext';
import { useCan } from '../auth/AuthContext';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { Modal, FormField, DatePicker, NumberField, Combobox, Button, IconButton, EntityCard, EntityAvatar, StatusPill, cardGridCls, inputCls, PageHeader, Toolbar, FilterChips, EmptyState, pageWrapCls } from '../components/ui';
import { IconPlus, IconEdit } from '../components/icons';
import {
    buildOccupancy, unitState, unitRate, tenancyRateCard, facilityLease, tenantName, byId,
    UNIT_STATE_STYLE, paidThruStatus, PAID_TONE_CLS, tenancyPlace, tenancyAddress, STORAGE_SIZES,
} from '../lib/storageHelpers';

// ===== STORAGE PAGE — v2 (sql/086, Jaylen 2026-09-08) =====
// Two halves:
//   • UNIT BOARD — every room BBM holds at each facility (inventory lives
//     on Settings → Storage), colored by state: empty / quoted / occupied /
//     BBM use. This is the "is 612 free?" glance.
//   • TENANCIES — who is in them. A tenancy = a client (or a name-only
//     tenant who has no client file yet) in one-or-more units, with what
//     they're billed, paid-thru, fullness, and the in/out estimate links.
//     Client-OWNED storage is a tenancy with no inventory units.
// Lifecycle unchanged from round 2: quoted → active → closed; the
// estimates trigger flips quoted → active when the move-in completes.

const FULLNESS_STEPS = [0, 25, 50, 75, 100];
const fullnessLabel = (f) => f === 0 ? 'Empty' : f === 100 ? 'Full' : `${f === 25 ? '¼' : f === 50 ? '½' : '¾'}`;
const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `$${(Math.round(n * 100) / 100).toLocaleString()}`;

// Simple package icon (no box glyph in the shared icon set). Imported by App.jsx for the nav.
export const IconPackage = ({ size = 18, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
        <path d="m3.3 7 8.7 5 8.7-5"/>
        <path d="M12 22V12"/>
    </svg>
);

function FullnessChips({ value, onChange, disabled = false, compact = false }) {
    return (
        <div className="flex gap-1">
            {FULLNESS_STEPS.map(f => (
                <button key={f} type="button" disabled={disabled}
                    onClick={() => onChange(f)}
                    title={`${f}% full`}
                    className={`${compact ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-xs'} font-bold rounded-lg border-2 transition-all ${value === f
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'}`}>
                    {fullnessLabel(f)}
                </button>
            ))}
        </div>
    );
}

// Client search-to-select (215+ clients). Used by the tenancy modal and
// by "link to client" on name-only tenants.
function ClientPicker({ clients, value, onChange, allowNameOnly = false, nameOnly = '', onNameOnly }) {
    const [q, setQ] = useState('');
    const selected = clients.find(c => c.id === value);
    const matches = useMemo(() => {
        const s = q.toLowerCase().trim();
        if (!s) return [];
        return clients.filter(c => (c.name || '').toLowerCase().includes(s)).sort((a, b) => (a.name || '').localeCompare(b.name || '')).slice(0, 8);
    }, [clients, q]);
    if (selected) {
        return (
            <div className="flex items-center gap-2">
                <span className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-800">{selected.name}</span>
                <Button variant="tertiary" size="sm" onClick={() => onChange('')}>change</Button>
            </div>
        );
    }
    return (
        <div>
            <input className={inputCls} value={q} onChange={e => setQ(e.target.value)} placeholder="Search client by name…" />
            {matches.length > 0 && (
                <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100 bg-white">
                    {matches.map(c => (
                        <button key={c.id} type="button" onClick={() => { onChange(c.id); setQ(''); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                            <span className="font-semibold text-slate-800">{c.name}</span>
                            {c.phone && <span className="text-xs text-slate-400 ml-2">{c.phone}</span>}
                        </button>
                    ))}
                </div>
            )}
            {allowNameOnly && (
                <div className="mt-2">
                    <div className="text-[10px] text-slate-400 mb-1">…or no client file yet — just a name:</div>
                    <input className={inputCls} value={nameOnly} onChange={e => onNameOnly(e.target.value)} placeholder="Tenant name (as on the facility log)" />
                </div>
            )}
        </div>
    );
}

// Unit multi-select for a facility: empty units + the ones this tenancy
// already holds. Grouped by size, cost shown.
function UnitPicker({ facility, units, occupancy, selected, onToggle, tenancyId }) {
    const groups = useMemo(() => {
        const g = {};
        units.filter(u => u.facility_id === facility?.id && u.active && u.kind === 'rental')
            .filter(u => { const t = occupancy[u.id]; return !t || t.id === tenancyId; })
            .forEach(u => { (g[u.size || 'other'] ||= []).push(u); });
        const order = (a, b) => { const ia = STORAGE_SIZES.indexOf(a), ib = STORAGE_SIZES.indexOf(b); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b); };
        return Object.keys(g).sort(order).map(size => ({ size, units: g[size] }));
    }, [units, facility?.id, occupancy, tenancyId]);
    if (!facility) return <p className="text-xs text-slate-400">Pick a facility first.</p>;
    if (groups.length === 0) return <p className="text-xs font-semibold text-red-600">No empty units at {facility.name}.</p>;
    return (
        <div className="space-y-1.5">
            {groups.map(g => (
                <div key={g.size} className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-500 w-24 shrink-0">{g.size}{facility.size_rates?.[g.size] ? <span className="font-normal text-slate-400"> · ${facility.size_rates[g.size]}</span> : null}</span>
                    {g.units.map(u => {
                        const on = selected.includes(u.id);
                        return (
                            <button key={u.id} type="button" onClick={() => onToggle(u.id)} title={u.notes || ''}
                                className={`px-2.5 py-1 rounded-lg border-2 text-xs font-mono font-bold transition-all ${on ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-emerald-200 text-slate-700 hover:border-indigo-300'}`}>
                                {u.unit_number}
                            </button>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

function TenancyModal({ tenancy, clients, facilities, units, occupancy, presetUnitId, onClose, onSave }) {
    const defaultFacility = facilities.find(f => f.is_default) || facilities[0] || null;
    const preset = presetUnitId ? units.find(u => u.id === presetUnitId) : null;
    const [form, setForm] = useState(() => tenancy ? {
        client_id: tenancy.client_id || '', tenant_name: tenancy.tenant_name || '', ownership: tenancy.ownership || 'bbm',
        facility_id: tenancy.facility_id || defaultFacility?.id || '', unit_ids: tenancy.unit_ids || [],
        facility_name: tenancy.facility_name || '', facility_address: tenancy.facility_address || '', unit_number: tenancy.unit_number || '',
        access_notes: tenancy.access_notes || '', fullness: tenancy.fullness ?? 0,
        monthly_cost: tenancy.monthly_cost ?? '', monthly_rate: tenancy.monthly_rate ?? '', paid_thru: tenancy.paid_thru || '',
        moved_in_at: tenancy.moved_in_at || '', expected_out: tenancy.expected_out || '', notes: tenancy.notes || '', status: tenancy.status,
    } : {
        client_id: '', tenant_name: '', ownership: 'bbm',
        facility_id: preset?.facility_id || defaultFacility?.id || '', unit_ids: preset ? [preset.id] : [],
        facility_name: '', facility_address: '', unit_number: '', access_notes: '', fullness: 0,
        monthly_cost: '', monthly_rate: '', paid_thru: '', moved_in_at: today(), expected_out: '', notes: '', status: 'active',
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const isBbm = form.ownership === 'bbm';
    const facility = facilities.find(f => f.id === form.facility_id) || null;
    const unitsById = byId(units);
    // Rate-card total for the picked units = default billed rate.
    const rateCard = isBbm ? form.unit_ids.reduce((s, id) => s + unitRate(unitsById[id], facility), 0) : 0;
    const hasTenant = !!form.client_id || form.tenant_name.trim().length > 0;

    const handleSave = async () => {
        if (!hasTenant) return;
        setSaving(true);
        const billed = form.monthly_rate === '' && rateCard > 0 ? rateCard : form.monthly_rate;
        try { await onSave({ ...form, monthly_rate: billed }); onClose(); }
        finally { setSaving(false); }
    };

    return (
        <Modal title={tenancy?.id ? 'Edit Storage Tenant' : 'Add Storage Tenant'} onClose={onClose} width="max-w-2xl">
            <div className="space-y-4">
                <FormField label="Who" required>
                    <ClientPicker clients={clients} value={form.client_id} onChange={v => set('client_id', v)}
                        allowNameOnly nameOnly={form.tenant_name} onNameOnly={v => set('tenant_name', v)} />
                    {!form.client_id && form.tenant_name.trim() && <p className="text-[10px] text-slate-400 mt-1">Name-only tenant — link a client file later from the card.</p>}
                </FormField>

                <FormField label="Whose unit is it?">
                    <div className="flex gap-2">
                        <button type="button" onClick={() => set('ownership', 'bbm')}
                            className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-bold transition-all ${isBbm ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                            BBM unit <span className="block text-[10px] font-normal">one of ours — pick from inventory</span>
                        </button>
                        <button type="button" onClick={() => set('ownership', 'client')}
                            className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-bold transition-all ${!isBbm ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                            Client's own <span className="block text-[10px] font-normal">their unit — we just track it</span>
                        </button>
                    </div>
                </FormField>

                {isBbm ? (
                    <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3 space-y-3">
                        {facilities.length > 1 && (
                            <FormField label="Facility">
                                <Combobox
                                    items={facilities.map(f => ({ value: f.id, label: f.name }))}
                                    value={form.facility_id}
                                    onChange={v => { set('facility_id', v); set('unit_ids', []); }}
                                    clearable={false}
                                    placeholder="Pick a facility…"
                                />
                            </FormField>
                        )}
                        {facilities.length === 0 && <p className="text-xs text-amber-700">No facility yet — add one on Settings → Storage.</p>}
                        <FormField label={`Units${form.unit_ids.length ? ` (${form.unit_ids.length} selected)` : ''}`}>
                            <UnitPicker facility={facility} units={units} occupancy={occupancy} selected={form.unit_ids} tenancyId={tenancy?.id}
                                onToggle={id => set('unit_ids', form.unit_ids.includes(id) ? form.unit_ids.filter(x => x !== id) : [...form.unit_ids, id])} />
                        </FormField>
                        <FormField label={`Client is billed ($/mo)${rateCard ? ` — rate card ${money(rateCard)}` : ''}`}>
                            <NumberField step={5} min={0} value={form.monthly_rate} onChange={v => set('monthly_rate', v)} placeholder={rateCard ? `${rateCard} (rate card — leave blank to use it)` : '0'} />
                        </FormField>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Facility"><input className={inputCls} value={form.facility_name} onChange={e => set('facility_name', e.target.value)} placeholder="Public Storage Asheville" /></FormField>
                            <FormField label="Unit #"><input className={inputCls} value={form.unit_number} onChange={e => set('unit_number', e.target.value)} placeholder="B-112" /></FormField>
                        </div>
                        <FormField label="Facility Address"><input className={inputCls} value={form.facility_address} onChange={e => set('facility_address', e.target.value)} placeholder="123 Storage Rd, Asheville, NC" /></FormField>
                        <FormField label="Access Notes"><input className={inputCls} value={form.access_notes} onChange={e => set('access_notes', e.target.value)} placeholder="Gate code · unit is around back" /></FormField>
                    </div>
                )}

                <div className="grid grid-cols-3 gap-3">
                    <FormField label="Paid through">
                        <DatePicker className={inputCls + ' cursor-pointer'} value={form.paid_thru} onChange={v => set('paid_thru', v)} placeholder="Select date…" />
                    </FormField>
                    <FormField label="Moved In">
                        <DatePicker className={inputCls + ' cursor-pointer'} value={form.moved_in_at} onChange={v => set('moved_in_at', v)} placeholder="Select date…" />
                    </FormField>
                    <FormField label="Expected Out">
                        <input className={inputCls} value={form.expected_out} onChange={e => set('expected_out', e.target.value)} placeholder='"Spring"' />
                    </FormField>
                </div>
                <FormField label="How full?"><FullnessChips value={form.fullness ?? 0} onChange={v => set('fullness', v)} /></FormField>
                <FormField label="Notes"><textarea rows="2" className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="What's in there, special handling…" /></FormField>

                <div className="flex justify-end gap-3 pt-2">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={saving} disabled={!hasTenant} onClick={handleSave}>
                        {tenancy?.id ? 'Save' : 'Add Tenant'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// Link a name-only tenant to a client file.
function LinkClientModal({ tenancy, clients, onClose, onLink }) {
    const [clientId, setClientId] = useState('');
    return (
        <Modal title={`Link "${tenancy.tenant_name}" to a client file`} onClose={onClose} width="max-w-md">
            <div className="space-y-4">
                <ClientPicker clients={clients} value={clientId} onChange={setClientId} />
                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" disabled={!clientId} onClick={() => onLink(clientId)}>Link</Button>
                </div>
            </div>
        </Modal>
    );
}

// Hoisted to module scope (2026-09-08 live test): defining these inside
// StoragePage gave them a new identity every render → every fullness tap
// remounted the whole grid and threw the page scroll back to the top.
function UnitTile({ unit, ctx }) {
    const { occupancy, clientsById, setModal } = ctx;
    const state = unitState(unit, occupancy);
    const style = UNIT_STATE_STYLE[state];
    const t = occupancy[unit.id];
    const who = state === 'internal' ? (unit.internal_label || 'BBM use') : t ? tenantName(t, clientsById) : '';
    const onClick = () => {
        if (state === 'empty') setModal({ presetUnitId: unit.id });
        else if (t) setModal({ tenancy: t });
    };
    return (
        <button type="button" onClick={onClick} disabled={state === 'internal' || state === 'retired'}
            title={[`Unit ${unit.unit_number}`, unit.size, who, unit.notes].filter(Boolean).join(' · ')}
            className={`text-left rounded-lg border-2 px-2 py-1.5 min-w-[84px] transition-all disabled:cursor-default ${style.cls} ${state === 'empty' ? 'hover:border-indigo-400' : state === 'internal' ? '' : 'hover:shadow-sm'}`}>
            <div className="flex items-center justify-between gap-1">
                <span className="font-mono font-black text-sm">{unit.unit_number}</span>
                <span className="text-[9px] font-bold opacity-70">{unit.size}</span>
            </div>
            <div className="text-[10px] font-semibold truncate leading-tight mt-0.5 min-h-[13px]">{who || (state === 'empty' ? '+ assign' : '')}</div>
        </button>
    );
}

// ── Tenancy card ──────────────────────────────────────────────────
function TenancyCard({ t, ctx }) {
    const { clientsById, unitsById, facilitiesById, getEstimate, canSeeMoney, navigateToClient, setModal, setLinking,
        setFullness, paidEditId, setPaidEditId, setPaidThru, creatingOutFor, createMoveOutEstimate, markInStorage, closeTenancy, reopen } = ctx;
    const client = t.client_id ? clientsById[t.client_id] : null;
    const inEst = t.in_estimate_id ? getEstimate(t.in_estimate_id) : null;
    const outEst = t.out_estimate_id ? getEstimate(t.out_estimate_id) : null;
    const isBbm = t.ownership === 'bbm';
    const isClosed = t.status === 'closed', isQuoted = t.status === 'quoted';
    const card = tenancyRateCard(t, unitsById, facilitiesById);
    const rate = parseFloat(t.monthly_rate) || 0;
    const paid = !isClosed && !isQuoted ? paidThruStatus(t.paid_thru) : null;
    const units = (t.unit_ids || []).map(id => unitsById[id]).filter(Boolean);
    const fac = facilitiesById[t.facility_id];
    // Header pill = lifecycle state; overdue rent takes the slot in rose.
    const status = isQuoted ? { label: 'Quoted', color: '#d97706' }
        : isClosed ? { label: 'Closed', color: '#64748b' }
        : paid?.tone === 'overdue' ? { label: paid.label, color: '#e11d48' }
        : { label: 'In storage', color: '#4f46e5' };
    const where = isBbm ? (fac?.name || t.facility_name || 'Facility TBD') : ([t.facility_name, t.unit_number && `Unit ${t.unit_number}`].filter(Boolean).join(' · ') || 'Facility not set');
    const facts = [];
    if (!isClosed && !isQuoted) {
        facts.push({ label: 'Paid thru', value: paidEditId === t.id ? (
            <span className="inline-flex items-center gap-1">
                <DatePicker className="border border-slate-300 rounded-md px-2 py-0.5 text-[11px] cursor-pointer" value={t.paid_thru || ''} onChange={v => setPaidThru(t, v)} placeholder="Paid through…" />
                <IconButton label="Cancel" size="sm" onClick={() => setPaidEditId(null)}>✕</IconButton>
            </span>
        ) : (
            <button type="button" onClick={() => setPaidEditId(t.id)} title="Set paid-through date"
                className={`px-2 py-0.5 rounded-md border text-[11px] font-bold ${paid ? PAID_TONE_CLS[paid.tone] : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                {paid ? (paid.tone === 'ok' ? paid.label : `${paid.label} · thru ${t.paid_thru}`) : 'paid thru: set…'}
            </button>
        ) });
    }
    if (t.moved_in_at) facts.push({ label: 'In', value: t.moved_in_at });
    if (t.expected_out) facts.push({ label: 'Expected out', value: t.expected_out });
    if (isClosed && t.closed_at) facts.push({ label: 'Closed', value: t.closed_at });
    if (t.access_notes) facts.push({ label: 'Access', value: `🔑 ${t.access_notes}` });
    if (isBbm && canSeeMoney && !isClosed && (card > 0 || rate > 0)) {
        facts.push({ label: 'Billed', value: rate ? `${money(rate)}/mo` : <span className="text-amber-600">not set</span>, mono: true });
        if (card > 0) facts.push({ label: 'Rate card', mono: true, value: <>
            {money(card)}
            {rate > 0 && rate !== card && (
                <span className={`ml-2 font-bold ${rate - card >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{rate - card > 0 ? '+' : '−'}{money(Math.abs(rate - card))} vs card</span>
            )}
        </> });
    }
    return (
        // One card recipe — lists.md §4 (EntityCard owns padding, radius, hover, muted).
        <EntityCard
            avatar={<EntityAvatar square text={<IconPackage size={18}/>} color={isBbm ? '#dc2626' : '#64748b'} />}
            title={client ? (
                <button type="button" onClick={() => navigateToClient && navigateToClient(client.id)} className="hover:text-red-600 hover:underline underline-offset-2 text-left">{client.name}</button>
            ) : (
                <>
                    {t.tenant_name || 'Unknown tenant'}
                    <button type="button" onClick={() => setLinking(t)} className="ml-2 text-[10px] font-semibold text-slate-400 hover:text-indigo-600 whitespace-nowrap underline underline-offset-2" title="No client file yet">no file · link</button>
                </>
            )}
            subtitle={`${isBbm ? 'BBM unit' : "Client's own"} · ${where}`}
            badge={<StatusPill label={status.label} color={status.color} />}
            facts={facts}
            muted={isClosed}
            footer={
                <div className="flex items-center gap-2 flex-wrap w-full">
                    {inEst && <span className="text-[10px] font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md" title="Move-in estimate">IN: {inEst.job_number}</span>}
                    {outEst ? (
                        <span className="text-[10px] font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md" title="Move-out estimate">
                            OUT: {outEst.job_number}{outEst.is_draft ? ' · draft' : ''}{outEst.move_date ? ` · ${outEst.move_date}` : ' · date TBD'}
                        </span>
                    ) : !isClosed && !isQuoted && (
                        <Button variant="secondary" size="sm" loading={creatingOutFor === t.id} onClick={() => createMoveOutEstimate(t)}
                            title={client ? '' : 'Link a client file first'}>
                            + Move-out estimate
                        </Button>
                    )}
                    <div className="flex-1" />
                    <Button variant="tertiary" size="sm" leftIcon={<IconEdit size={14}/>} onClick={() => setModal({ tenancy: t })}>Edit</Button>
                    {isQuoted && (
                        <Button variant="primary" size="sm" onClick={() => markInStorage(t)}>✓ Mark In Storage</Button>
                    )}
                    {isClosed
                        ? <Button variant="tertiary" size="sm" onClick={() => reopen(t)}>Reopen</Button>
                        : <Button variant="danger" size="sm" onClick={() => closeTenancy(t)}>{isQuoted ? 'Void quote' : 'Close'}</Button>}
                </div>
            }
        >
            {isBbm && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    {units.length === 0
                        ? (isClosed
                            ? <span className="text-[11px] text-slate-400">no unit on record</span>
                            : <button type="button" onClick={() => setModal({ tenancy: t })} className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-0.5 hover:bg-amber-100">unit not assigned yet → pick one</button>)
                        : units.sort((a, b) => a.sort_order - b.sort_order).map(u => (
                            <span key={u.id} className="text-[11px] font-mono font-bold bg-indigo-50 text-indigo-800 border border-indigo-100 rounded-md px-2 py-0.5" title={u.size}>{u.unit_number}<span className="font-sans font-normal text-indigo-400"> {u.size}</span></span>
                        ))}
                </div>
            )}

            {!isClosed && !isQuoted && (
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Fullness</span>
                    <FullnessChips compact value={t.fullness ?? 0} onChange={f => setFullness(t, f)} />
                </div>
            )}
            {isQuoted && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                    Being quoted into storage — goes <b>In Storage</b> automatically when the move-in estimate completes.
                </div>
            )}

            {t.notes && <div className="text-[11px] text-slate-500 italic">{t.notes}</div>}
        </EntityCard>
    );
}


export default function StoragePage() {
    const { storageFacilities, storageUnits, storageTenancies, clients, estimates, saveStorageTenancy, patchStorageTenancy, saveEstimate } = useStore();
    const { navigateToClient, setCurrentPage } = useNav();
    const can = useCan();
    const canSeeMoney = can('view_financials');
    const { show: showToast } = useToast();
    const { confirm, confirmElement } = useConfirm();
    const [modal, setModal] = useState(null); // { tenancy?, presetUnitId? }
    const [linking, setLinking] = useState(null);
    const [showClosed, setShowClosed] = useState(false);
    // Toolbar chip: which tenancy list(s) to show — all / quoted / active / closed.
    const [tenancyFilter, setTenancyFilter] = useState('all');
    const [creatingOutFor, setCreatingOutFor] = useState(null);
    const [paidEditId, setPaidEditId] = useState(null);

    const clientsById = useMemo(() => byId(clients), [clients]);
    const unitsById = useMemo(() => byId(storageUnits), [storageUnits]);
    const facilitiesById = useMemo(() => byId(storageFacilities), [storageFacilities]);
    const occupancy = useMemo(() => buildOccupancy(storageTenancies), [storageTenancies]);
    const getEstimate = (id) => estimates.find(e => e.id === id);
    const facilities = useMemo(() => storageFacilities.filter(f => f.active !== false), [storageFacilities]);

    const quoted = storageTenancies.filter(t => t.status === 'quoted');
    const active = useMemo(() => storageTenancies.filter(t => t.status === 'active').sort((a, b) => {
        const pa = paidThruStatus(a.paid_thru)?.days ?? -9999, pb = paidThruStatus(b.paid_thru)?.days ?? -9999;
        return pb - pa || tenantName(a, clientsById).localeCompare(tenantName(b, clientsById));
    }), [storageTenancies, clientsById]);
    const closed = storageTenancies.filter(t => t.status === 'closed');

    // Money: BBM pays a flat LEASE per facility (empties included — that's
    // the real cost of the block); billed = what active tenants pay.
    const totals = useMemo(() => {
        const heldUnits = storageUnits.filter(u => u.active);
        const cost = facilities.reduce((s, f) => s + facilityLease(f), 0);
        const billed = active.filter(t => t.ownership === 'bbm').reduce((s, t) => s + (parseFloat(t.monthly_rate) || 0), 0);
        const states = { empty: 0, quoted: 0, occupied: 0, internal: 0 };
        heldUnits.forEach(u => { const st = unitState(u, occupancy); if (states[st] != null) states[st]++; });
        const overdue = active.filter(t => paidThruStatus(t.paid_thru)?.tone === 'overdue').length;
        return { cost, billed, net: billed - cost, held: heldUnits.length, states, overdue };
    }, [storageUnits, active, occupancy, facilities]);

    // Header summary line (page-headers.md): live counts, not prose.
    const summary = `${totals.held} unit${totals.held === 1 ? '' : 's'} held · ${totals.states.occupied} occupied · ${totals.states.empty} empty · ${active.length} tenant${active.length === 1 ? '' : 's'} in storage${totals.overdue > 0 ? ` · ${totals.overdue} past due` : ''}`;

    // ── Mutations (all patch, never full-row) ─────────────────────────
    const EDITABLE = ['client_id', 'tenant_name', 'ownership', 'facility_id', 'unit_ids', 'facility_name', 'facility_address', 'unit_number',
        'access_notes', 'fullness', 'monthly_rate', 'paid_thru', 'moved_in_at', 'expected_out', 'notes'];
    const handleSave = async (form) => {
        try {
            const clean = Object.fromEntries(EDITABLE.map(k => [k, form[k]]));
            if (clean.ownership !== 'bbm') { clean.facility_id = null; clean.unit_ids = []; }
            else { clean.facility_name = ''; clean.facility_address = ''; clean.unit_number = ''; }
            if (clean.client_id) clean.tenant_name = clean.tenant_name || '';
            if (modal?.tenancy?.id) { await patchStorageTenancy(modal.tenancy.id, clean); showToast('Storage tenant updated'); }
            else { await saveStorageTenancy({ ...clean, status: form.status || 'active' }); showToast('Storage tenant added'); }
        } catch (e) { showToast('Error: ' + e.message, 'error'); throw e; }
    };
    const setFullness = async (t, f) => { try { await patchStorageTenancy(t.id, { fullness: f }); } catch (e) { showToast('Error: ' + e.message, 'error'); } };
    const setPaidThru = async (t, v) => { try { await patchStorageTenancy(t.id, { paid_thru: v || null }); setPaidEditId(null); showToast('Paid-through updated'); } catch (e) { showToast('Error: ' + e.message, 'error'); } };
    const closeTenancy = async (t) => {
        const name = tenantName(t, clientsById);
        const isQuote = t.status === 'quoted';
        const unitsWord = `unit${(t.unit_ids || []).length > 1 ? 's' : ''}`;
        if (!(await confirm({
            title: isQuote ? `Void ${name}'s storage quote?` : `Close ${name}'s storage?`,
            body: isQuote
                ? `${name}'s reserved ${unitsWord} go back to empty and the quote moves to the Closed list. The rate and notes are kept on the record.`
                : `${name} moves to the Closed list and their ${unitsWord} show as empty — use this once their items are fully out. You can undo from the toast or reopen later.`,
            confirmLabel: isQuote ? 'Void quote' : 'Close storage',
            danger: isQuote,
        }))) return;
        try {
            await patchStorageTenancy(t.id, { status: 'closed', closed_at: today() });
            // 1B: closing is reversible — Undo puts the tenant back on the active list.
            showToast(t.status === 'quoted' ? 'Quote voided' : 'Closed', undefined, { undo: () => reopen(t) });
        }
        catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const markInStorage = async (t) => {
        try { await patchStorageTenancy(t.id, { status: 'active', moved_in_at: t.moved_in_at || today() }); showToast('Marked in storage'); }
        catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const reopen = async (t) => {
        // Refuse if any of its units got re-let while closed.
        const clash = (t.unit_ids || []).find(id => occupancy[id] && occupancy[id].id !== t.id);
        if (clash) { showToast(`Unit ${unitsById[clash]?.unit_number || ''} is now held by ${tenantName(occupancy[clash], clientsById)} — edit units before reopening`, 'error'); return; }
        try { await patchStorageTenancy(t.id, { status: 'active', closed_at: null }); showToast('Reopened'); }
        catch (e) { showToast('Error: ' + e.message, 'error'); }
    };
    const linkClient = async (t, clientId) => {
        try { await patchStorageTenancy(t.id, { client_id: clientId }); setLinking(null); showToast(`Linked to ${clientsById[clientId]?.name || 'client'}`); }
        catch (e) { showToast('Error: ' + e.message, 'error'); }
    };

    // "+ Move-out estimate" — creates a DATELESS DRAFT estimate prefilled
    // from the tenancy and links it back. Needs a client file.
    const createMoveOutEstimate = async (t) => {
        const client = t.client_id ? clientsById[t.client_id] : null;
        if (!client) { showToast('Link this tenant to a client file first', 'error'); return; }
        setCreatingOutFor(t.id);
        try {
            const originAddr = tenancyAddress(t, unitsById, facilitiesById);
            const place = tenancyPlace(t, unitsById, facilitiesById);
            const created = await saveEstimate({
                client_id: t.client_id,
                status: 'building',
                is_draft: true,
                job_type: 'LOCAL',
                origin_address: originAddr,
                destination_address: '',
                move_date: null,
                description: `Move-out of storage (${place}) — date TBD${t.expected_out ? ` (${t.expected_out})` : ''}`,
                internal_notes: 'Auto-created from the Storage page as the move-out half of a storage job. Date + destination land when the client calls.',
                planned_phases: [],
            });
            await patchStorageTenancy(t.id, { out_estimate_id: created.id });
            showToast(`Draft move-out estimate ${created.job_number || ''} created — price it from ${client.name}'s file`);
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        finally { setCreatingOutFor(null); }
    };

    // ── Unit board ────────────────────────────────────────────────────
    const grid = cardGridCls; // lists.md §4 — the shared entity-card grid
    const ctx = { occupancy, clientsById, unitsById, facilitiesById, getEstimate, canSeeMoney, navigateToClient, setModal, setLinking,
        setFullness, paidEditId, setPaidEditId, setPaidThru, creatingOutFor, createMoveOutEstimate, markInStorage, closeTenancy, reopen };

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Storage"
                helpTitle="How storage tracking works"
                summary={summary}
                help={<>
                    <p>The <span className="font-semibold">unit board</span> shows every room BBM rents at each facility — green is empty, indigo is occupied, amber is reserved by a quote, grey is BBM's own use. The inventory itself (unit numbers, sizes, the client rate card, and the lease we pay) lives on <span className="font-semibold">Settings → Storage</span>.</p>
                    <p>Below it, <span className="font-semibold">tenants</span>: who is in which unit(s), what they're billed, and when they're paid through — overdue rent floats to the top in red. A tenant can be a client file or just a name from the facility log; link the name to a client file whenever one gets created.</p>
                    <p>Lifecycle: <span className="font-semibold">Quoted</span> (created from an estimate's "Into storage" — the picked units are reserved but nothing is stored yet) → <span className="font-semibold">In Storage</span> (automatic when the move-in estimate completes, or Mark In Storage by hand) → <span className="font-semibold">Closed</span> (items fully out; units go back to empty).</p>
                    <p>The move-OUT half: quote it from the client's estimate tab with "Out of storage" (the unit becomes the origin), or <span className="font-semibold">+ Move-out estimate</span> here creates a dateless draft that waits until the client calls with a date.</p>
                </>}
                actions={<>
                    <Button variant="secondary" onClick={() => setCurrentPage && setCurrentPage('settings')}>Manage units &amp; facilities</Button>
                    <Button variant="primary" leftIcon={<IconPlus size={18}/>} onClick={() => setModal({})}>
                        Add Tenant
                    </Button>
                </>}
            />

            {/* Tenancy chips — one toolbar card (page-headers.md). */}
            <Toolbar>
                <FilterChips
                    ariaLabel="Filter tenants"
                    items={[
                        { key: 'all', label: 'All', count: storageTenancies.length },
                        { key: 'quoted', label: 'Quoted', count: quoted.length, color: '#d97706', bg: '#fffbeb' },
                        { key: 'active', label: 'In storage', count: active.length, color: '#4f46e5', bg: '#eef2ff' },
                        { key: 'closed', label: 'Closed', count: closed.length, color: '#64748b', bg: '#f1f5f9' },
                    ]}
                    value={tenancyFilter}
                    onChange={setTenancyFilter}
                />
            </Toolbar>

            {/* Totals strip */}
            <div className="flex flex-wrap gap-3">
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Units held</div>
                    <div className="text-xl font-bold text-slate-900">{totals.held}
                        <span className="text-xs text-slate-400 font-medium ml-1.5">{totals.states.occupied} occupied · {totals.states.empty} empty{totals.states.quoted ? ` · ${totals.states.quoted} quoted` : ''}{totals.states.internal ? ` · ${totals.states.internal} BBM use` : ''}</span>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Tenants</div>
                    <div className="text-xl font-bold text-slate-900">{active.length}{totals.overdue > 0 && <span className="text-xs font-bold text-rose-600 ml-1.5">{totals.overdue} past due</span>}</div>
                </div>
                {canSeeMoney && totals.held > 0 && (
                    <>
                        <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">BBM pays / mo</div>
                            <div className="text-xl font-bold text-slate-900">{money(totals.cost)}<span className="text-[10px] text-slate-400 font-medium ml-1.5">lease · {totals.held ? `~${money(Math.round(totals.cost / totals.held))}/unit` : ''}</span></div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Tenants billed / mo</div>
                            <div className="text-xl font-bold text-slate-900">{money(totals.billed)}</div>
                        </div>
                        <div className={`bg-white border rounded-xl px-4 py-2.5 ${totals.net >= 0 ? 'border-emerald-200' : 'border-rose-200'}`}>
                            <div className={`text-[10px] font-bold uppercase tracking-wide ${totals.net >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>Net / mo</div>
                            <div className={`text-xl font-bold ${totals.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{totals.net >= 0 ? '+' : '−'}{money(Math.abs(totals.net))}</div>
                        </div>
                    </>
                )}
            </div>

            {/* Unit board */}
            {facilities.length === 0 ? (
                <EmptyState
                    icon={<IconPackage size={28}/>}
                    title="No storage facility set up yet"
                    hint="Add Extra Space and its units on Settings → Storage; the unit board fills in from there."
                    action={<Button variant="secondary" size="sm" onClick={() => setCurrentPage && setCurrentPage('settings')}>Go to Settings</Button>}
                />
            ) : facilities.map(f => {
                const units = storageUnits.filter(u => u.facility_id === f.id && u.active).sort((a, b) => (a.sort_order - b.sort_order) || String(a.unit_number).localeCompare(String(b.unit_number)));
                return (
                    <div key={f.id} className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                            <div>
                                <span className="font-bold text-slate-900">{f.name}</span>
                                {f.address && <span className="text-xs text-slate-400 ml-2">{f.address}</span>}
                            </div>
                            <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500">
                                {['empty', 'quoted', 'occupied', 'internal'].map(k => (
                                    <span key={k} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${UNIT_STATE_STYLE[k].dot}`}/>{UNIT_STATE_STYLE[k].label}</span>
                                ))}
                            </div>
                        </div>
                        {units.length === 0
                            ? <div className="text-sm text-slate-400">No units at this facility yet — add them on Settings → Storage.</div>
                            : <div className="flex flex-wrap gap-1.5">{units.map(u => <UnitTile key={u.id} unit={u} ctx={ctx} />)}</div>}
                    </div>
                );
            })}

            {/* Tenancies — sections gated by the toolbar chip. */}
            {(tenancyFilter === 'all' || tenancyFilter === 'quoted') && (quoted.length > 0 || tenancyFilter === 'quoted') && (
                <div>
                    <div className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">Quoted — not in storage yet ({quoted.length})</div>
                    {quoted.length === 0 ? (
                        <EmptyState
                            icon={<IconPackage size={28}/>}
                            title="No quoted tenants match"
                            hint="Nothing is reserved by a quote right now."
                            action={<Button variant="tertiary" size="sm" onClick={() => setTenancyFilter('all')}>Clear filters</Button>}
                        />
                    ) : <div className={grid}>{quoted.map(t => <TenancyCard key={t.id} t={t} ctx={ctx} />)}</div>}
                </div>
            )}

            {(tenancyFilter === 'all' || tenancyFilter === 'active') && (
                <div>
                    <div className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-2">In storage ({active.length})</div>
                    {active.length === 0 ? (
                        <EmptyState
                            icon={<IconPackage size={28}/>}
                            title="Nobody in storage right now"
                            hint='Quote a move "Into storage" from an estimate, tap an empty unit above, or add a tenant by hand.'
                            action={<Button size="sm" leftIcon={<IconPlus size={16}/>} onClick={() => setModal({})}>Add Tenant</Button>}
                        />
                    ) : <div className={grid}>{active.map(t => <TenancyCard key={t.id} t={t} ctx={ctx} />)}</div>}
                </div>
            )}

            {tenancyFilter === 'all' && closed.length > 0 && (
                <div>
                    <button type="button" onClick={() => setShowClosed(s => !s)} className="text-xs font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wide">
                        {showClosed ? '▾' : '▸'} Closed ({closed.length})
                    </button>
                    {showClosed && <div className={`${grid} mt-3`}>{closed.map(t => <TenancyCard key={t.id} t={t} ctx={ctx} />)}</div>}
                </div>
            )}
            {tenancyFilter === 'closed' && (
                <div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Closed ({closed.length})</div>
                    {closed.length === 0 ? (
                        <EmptyState
                            icon={<IconPackage size={28}/>}
                            title="No closed tenants match"
                            hint="Nobody has moved out of storage yet."
                            action={<Button variant="tertiary" size="sm" onClick={() => setTenancyFilter('all')}>Clear filters</Button>}
                        />
                    ) : <div className={grid}>{closed.map(t => <TenancyCard key={t.id} t={t} ctx={ctx} />)}</div>}
                </div>
            )}

            {modal && (
                <TenancyModal
                    tenancy={modal.tenancy || null}
                    presetUnitId={modal.presetUnitId || null}
                    clients={clients} facilities={facilities} units={storageUnits} occupancy={occupancy}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}
            {linking && <LinkClientModal tenancy={linking} clients={clients} onClose={() => setLinking(null)} onLink={(cid) => linkClient(linking, cid)} />}
            {confirmElement}
        </div>
    );
}
